import { Check, Download, X, ChevronLeft, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { toast } from "react-toastify";

// PDF Preview Component
const PDFPreview = ({ document }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [imageError, setImageError] = useState(false);

  const getPDFImageUrl = (pageNumber = 1) => {
    const pdfUrl = document.filePath || document.fileUrl;
    
    // Check if it's a Cloudinary URL
    if (pdfUrl.includes('cloudinary.com') && pdfUrl.includes('/upload/')) {
      // Transform Cloudinary PDF to image
      const parts = pdfUrl.split('/upload/');
      if (parts.length === 2) {
        return `${parts[0]}/upload/f_jpg,pg_${pageNumber},w_400,h_600,c_fit/${parts[1]}`;
      }
    }
    
    // For non-Cloudinary URLs, try Google Docs viewer as fallback
    return `https://docs.google.com/gview?url=${encodeURIComponent(pdfUrl)}&embedded=true`;
  };

  const handleImageLoad = () => {
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  if (imageError) {
    return (
      <div className="w-full h-64 rounded border flex flex-col items-center justify-center text-white bg-gray-800">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#FFCA20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <polyline points="14,2 14,8 20,8" stroke="#FFCA20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="16" y1="13" x2="8" y2="13" stroke="#FFCA20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="16" y1="17" x2="8" y2="17" stroke="#FFCA20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <p className="mt-2 text-sm">PDF Preview</p>
        <p className="text-xs text-gray-400 mt-1">Click download to view document</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-center mb-2">
        <img
          src={getPDFImageUrl(currentPage)}
          alt={`${document.documentType} Preview - Page ${currentPage}`}
          className="rounded object-contain border bg-white"
          style={{ maxHeight: "500px", maxWidth: "100%" }}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      </div>
      
      {/* Page navigation for multi-page PDFs */}
      <div className="flex items-center justify-center gap-4 mt-2">
        <button
          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
          disabled={currentPage <= 1}
          className="p-1 text-[#FFCA20] disabled:text-gray-500 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={16} />
        </button>
        
        <span className="text-white text-sm">
          Page {currentPage} of {totalPages}
        </span>
        
        <button
          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage >= totalPages}
          className="p-1 text-[#FFCA20] disabled:text-gray-500 disabled:cursor-not-allowed"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

const DocumentView = ({
  REQUIRED_DOCUMENTS,
  documentTypeMap,
  document,
  application,
  sendNotification,
  isOpen,
  onClose,
  handleVerifyDocument,
}) => {

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#131313] bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#131313] rounded-lg overflow-hidden w-full max-w-2xl border border-gray-800 shadow-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b border-[#333333] flex justify-between items-center">
          <div className="flex flex-col">
            <div className="flex items-center mb-1">
              <span className="text-white text-md mr-2">📄</span>
              <h3 className="text-white text-lg font-medium">
                {(() => {
                  console.log('Document type:', document.documentType);
                  console.log('Document type map:', documentTypeMap);
                  
                  // Direct mapping for common document types
                  const typeLabels = {
                    'AADHAR_CARD': 'Aadhaar Card',
                    'PAN_CARD': 'PAN Card',
                    'LIVE_SELFIE': 'Live Selfie',
                    'CANCELED_CHEQUE': 'Cancelled Cheque',
                    'BANK_STATEMENT': 'Bank Statement',
                    'SALARY_SLIPS': 'Salary Slip',
                    'INCOME_TAX_RETURN': 'Income Tax Returns',
                    'GST_DOCUMENT': 'GST Document',
                    'COMPANY_REGISTRATION_LETTER': 'Company Registration Letter',
                    'MSME_UDYAM_LICENSE': 'MSME Udyam License',
                    'MUNICIPAL_LICENSE': 'Municipal License',
                    'RENTAL_AGREEMENT': 'Rental Agreement'
                  };
                  
                  return documentTypeMap[document.documentType] || 
                         typeLabels[document.documentType] || 
                         document.documentType?.replace(/_/g, ' ') || 
                         'Document';
                })()}
              </h3>
            </div>
            <p className="text-gray-400 text-sm">
              {document.originalFileName || 
               (document.filePath || document.fileUrl)?.split('/').pop()?.split('?')[0] || 
               'Document file'}
            </p>
          </div>
          <div className="flex items-center">
            <button
              className="text-[#FFCA20] flex items-center text-md hover:text-[#FFCA20] transition-colors mr-4"
              onClick={() => {
                if (document && (document.fileUrl || document.filePath)) {
                  const link = window.document.createElement("a"); // Use window.document

                  // Create the docTypeToLabelMap inside the onClick handler or ensure it's correctly scoped
                  // to access the current document's type.
                  const employmentType =
                    application?.user?.employmentType || "salaried";
                  const currentDocList =
                    REQUIRED_DOCUMENTS[employmentType] ||
                    REQUIRED_DOCUMENTS.default;
                  const docTypeToLabelMap = currentDocList.reduce(
                    (map, docInfo) => {
                      map[docInfo.type] = docInfo.label.replace(/\s+/g, "_"); // Sanitize label for filename
                      return map;
                    },
                    {}
                  );

                  // Use user name from the specific document object, and the documentType
                  const userNameForFile =
                    document?.user?.name?.replace(/\s+/g, "_") || "user";
                  // Get the label for the current document's type
                  const documentLabelForFile =
                    docTypeToLabelMap[document.documentType] ||
                    document.documentType.replace(/\s+/g, "_") ||
                    "document";

                  const fileUrlForExt =
                    document.fileUrl || document.filePath || ""; // Corrected: Use 'document'
                  const urlPartsForExt = fileUrlForExt.split(".");
                  const extension =
                    urlPartsForExt.length > 1
                      ? urlPartsForExt.pop().split("?")[0]
                      : "file";

                  const filenameToUse = `${userNameForFile}-${documentLabelForFile}.${extension}`;
                  link.download = filenameToUse;

                  // Prefer fileUrl if available, otherwise filePath
                  let downloadUrl = document.fileUrl || document.filePath; // Corrected: Use 'document'

                  // Cloudinary transformation for forced download
                  if (downloadUrl.includes("/upload/")) {
                    const parts = downloadUrl.split("/upload/");
                    if (parts.length === 2) {
                      downloadUrl = `${parts[0]}/upload/fl_attachment/${parts[1]}`;
                    }
                  }

                  link.href = downloadUrl;
                  window.document.body.appendChild(link); // Use window.document
                  link.click();
                  window.document.body.removeChild(link); // Use window.document

                  toast.success("Document download initiated.");
                } else {
                  toast.error("Document file not available for download.");
                }
              }}
            >
              Download <Download className="w-4 h-4 ml-2" />
            </button>
            {document?.status === "verified" && (
              <button
                className="text-red-500 flex items-center text-md hover:text-red-400 transition-colors mr-4"
                onClick={() => {
                  handleVerifyDocument(document._id, "rejected");
                }}
              >
                Reject 
              </button>
            )}
            <button
              className="text-gray-400 hover:text-white "
              onClick={onClose}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Document details */}
        <div className=" p-2 flex gap-1 border-b border-[#333333]">
          <div className="border-r border-[#333333] pr-4">
            <p className="text-gray-400 text-xs">Full Name</p>
            <p className="text-white">{document?.user?.name}</p>
          </div>
          <div className="pl-4">
            <p className="text-gray-400 text-xs">Document Number</p>
            <p className="text-white">{document?.documentNumber}</p>
          </div>
        </div>

        {/* Document preview */}
        <div className="flex justify-center p-4 bg-[#131313]">
          {(document.filePath?.toLowerCase().includes('.pdf') || document.fileUrl?.toLowerCase().includes('.pdf')) ? (
            <PDFPreview document={document} />
          ) : (
            <img
              src={document.filePath || document.fileUrl}
              alt={`${document.documentType} Preview`}
              className="rounded object-contain"
              style={{ maxHeight: "210px" }}
              onError={(e) => {
                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE0IDJINmEyIDIgMCAwIDAtMiAydjE2YTIgMiAwIDAgMCAyIDJoMTJhMiAyIDAgMCAwIDItMlY4eiIgc3Ryb2tlPSIjRkZDQTIwIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8cG9seWxpbmUgcG9pbnRzPSIxNCwyIDE0LDggMjAsOCIgc3Ryb2tlPSIjRkZDQTIwIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4K';
                e.target.alt = 'Document preview not available';
              }}
            />
          )}
        </div>

        {/* Action buttons */}

        {document?.status !== "rejected" && document?.status !== "verified" && (
          <div className="p-4 flex justify-end space-x-4">
            <button
              onClick={() => {
                handleVerifyDocument(document._id, "rejected");
              }}
            >
              <svg
                width="96"
                height="43"
                viewBox="0 0 96 43"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="0.5"
                  y="0.5"
                  width="95"
                  height="42"
                  rx="3.5"
                  stroke="#FF0000"
                />
                <path
                  d="M13.2784 27V15.3636H17.4261C18.3277 15.3636 19.0758 15.5189 19.6705 15.8295C20.2689 16.1402 20.7159 16.5701 21.0114 17.1193C21.3068 17.6648 21.4545 18.2955 21.4545 19.0114C21.4545 19.7235 21.3049 20.3504 21.0057 20.892C20.7102 21.4299 20.2633 21.8485 19.6648 22.1477C19.0701 22.447 18.322 22.5966 17.4205 22.5966H14.2784V21.0852H17.2614C17.8295 21.0852 18.2917 21.0038 18.6477 20.8409C19.0076 20.678 19.2708 20.4413 19.4375 20.1307C19.6042 19.8201 19.6875 19.447 19.6875 19.0114C19.6875 18.572 19.6023 18.1913 19.4318 17.8693C19.2652 17.5473 19.0019 17.3011 18.642 17.1307C18.286 16.9564 17.8182 16.8693 17.2386 16.8693H15.0341V27H13.2784ZM19.0227 21.75L21.8977 27H19.8977L17.0795 21.75H19.0227ZM27.1065 27.1761C26.2467 27.1761 25.5062 26.9924 24.8849 26.625C24.2675 26.2538 23.7902 25.733 23.4531 25.0625C23.1198 24.3883 22.9531 23.5985 22.9531 22.6932C22.9531 21.7992 23.1198 21.0114 23.4531 20.3295C23.7902 19.6477 24.2599 19.1155 24.8622 18.733C25.4683 18.3504 26.1766 18.1591 26.9872 18.1591C27.4796 18.1591 27.9569 18.2405 28.419 18.4034C28.8812 18.5663 29.2959 18.822 29.6634 19.1705C30.0308 19.5189 30.3205 19.9716 30.5327 20.5284C30.7448 21.0814 30.8509 21.7538 30.8509 22.5455V23.1477H23.9134V21.875H29.1861C29.1861 21.428 29.0952 21.0322 28.9134 20.6875C28.7315 20.339 28.4759 20.0644 28.1463 19.8636C27.8205 19.6629 27.438 19.5625 26.9986 19.5625C26.5213 19.5625 26.1046 19.6799 25.7486 19.9148C25.3963 20.1458 25.1236 20.4489 24.9304 20.8239C24.741 21.1951 24.6463 21.5985 24.6463 22.0341V23.0284C24.6463 23.6117 24.7486 24.108 24.9531 24.517C25.1615 24.9261 25.4512 25.2386 25.8224 25.4545C26.1937 25.6667 26.6274 25.7727 27.1236 25.7727C27.4455 25.7727 27.7391 25.7273 28.0043 25.6364C28.2694 25.5417 28.4986 25.4015 28.6918 25.2159C28.8849 25.0303 29.0327 24.8011 29.1349 24.5284L30.7429 24.8182C30.6141 25.2917 30.383 25.7064 30.0497 26.0625C29.7202 26.4148 29.3054 26.6894 28.8054 26.8864C28.3092 27.0795 27.7429 27.1761 27.1065 27.1761ZM32.7358 18.2727H34.4347V27.5682C34.4347 28.1515 34.3286 28.6439 34.1165 29.0455C33.9081 29.447 33.5975 29.7519 33.1847 29.9602C32.7756 30.1686 32.2699 30.2727 31.6676 30.2727C31.607 30.2727 31.5502 30.2727 31.4972 30.2727C31.4403 30.2727 31.3816 30.2708 31.321 30.267V28.8068C31.3741 28.8068 31.4214 28.8068 31.4631 28.8068C31.5009 28.8068 31.5445 28.8068 31.5938 28.8068C31.9953 28.8068 32.285 28.6989 32.4631 28.483C32.6449 28.2708 32.7358 27.9621 32.7358 27.5568V18.2727ZM33.5767 16.9261C33.2813 16.9261 33.0275 16.8277 32.8153 16.6307C32.607 16.4299 32.5028 16.1913 32.5028 15.9148C32.5028 15.6345 32.607 15.3958 32.8153 15.1989C33.0275 14.9981 33.2813 14.8977 33.5767 14.8977C33.8722 14.8977 34.1241 14.9981 34.3324 15.1989C34.5445 15.3958 34.6506 15.6345 34.6506 15.9148C34.6506 16.1913 34.5445 16.4299 34.3324 16.6307C34.1241 16.8277 33.8722 16.9261 33.5767 16.9261ZM40.4815 27.1761C39.6217 27.1761 38.8812 26.9924 38.2599 26.625C37.6425 26.2538 37.1652 25.733 36.8281 25.0625C36.4948 24.3883 36.3281 23.5985 36.3281 22.6932C36.3281 21.7992 36.4948 21.0114 36.8281 20.3295C37.1652 19.6477 37.6349 19.1155 38.2372 18.733C38.8433 18.3504 39.5516 18.1591 40.3622 18.1591C40.8546 18.1591 41.3319 18.2405 41.794 18.4034C42.2562 18.5663 42.6709 18.822 43.0384 19.1705C43.4058 19.5189 43.6955 19.9716 43.9077 20.5284C44.1198 21.0814 44.2259 21.7538 44.2259 22.5455V23.1477H37.2884V21.875H42.5611C42.5611 21.428 42.4702 21.0322 42.2884 20.6875C42.1065 20.339 41.8509 20.0644 41.5213 19.8636C41.1955 19.6629 40.813 19.5625 40.3736 19.5625C39.8963 19.5625 39.4796 19.6799 39.1236 19.9148C38.7713 20.1458 38.4986 20.4489 38.3054 20.8239C38.116 21.1951 38.0213 21.5985 38.0213 22.0341V23.0284C38.0213 23.6117 38.1236 24.108 38.3281 24.517C38.5365 24.9261 38.8262 25.2386 39.1974 25.4545C39.5687 25.6667 40.0024 25.7727 40.4986 25.7727C40.8205 25.7727 41.1141 25.7273 41.3793 25.6364C41.6444 25.5417 41.8736 25.4015 42.0668 25.2159C42.2599 25.0303 42.4077 24.8011 42.5099 24.5284L44.1179 24.8182C43.9891 25.2917 43.758 25.7064 43.4247 26.0625C43.0952 26.4148 42.6804 26.6894 42.1804 26.8864C41.6842 27.0795 41.1179 27.1761 40.4815 27.1761ZM49.7869 27.1761C48.9422 27.1761 48.215 26.9848 47.6051 26.6023C46.9991 26.2159 46.5331 25.6837 46.2074 25.0057C45.8816 24.3277 45.7188 23.5511 45.7188 22.6761C45.7188 21.7898 45.8854 21.0076 46.2188 20.3295C46.5521 19.6477 47.0218 19.1155 47.6278 18.733C48.2339 18.3504 48.9479 18.1591 49.7699 18.1591C50.4328 18.1591 51.0237 18.2822 51.5426 18.5284C52.0616 18.7708 52.4801 19.1117 52.7983 19.5511C53.1203 19.9905 53.3116 20.5038 53.3722 21.0909H51.7188C51.6278 20.6818 51.4195 20.3295 51.0938 20.0341C50.7718 19.7386 50.34 19.5909 49.7983 19.5909C49.3248 19.5909 48.91 19.7159 48.554 19.9659C48.2017 20.2121 47.9271 20.5644 47.7301 21.0227C47.5331 21.4773 47.4347 22.0152 47.4347 22.6364C47.4347 23.2727 47.5313 23.822 47.7244 24.2841C47.9176 24.7462 48.1903 25.1042 48.5426 25.358C48.8987 25.6117 49.3172 25.7386 49.7983 25.7386C50.1203 25.7386 50.4119 25.6799 50.6733 25.5625C50.9384 25.4413 51.16 25.2689 51.3381 25.0455C51.5199 24.822 51.6468 24.553 51.7188 24.2386H53.3722C53.3116 24.803 53.1278 25.3068 52.821 25.75C52.5142 26.1932 52.1032 26.5417 51.5881 26.7955C51.0767 27.0492 50.4763 27.1761 49.7869 27.1761ZM59.2628 18.2727V19.6364H54.4957V18.2727H59.2628ZM55.7741 16.1818H57.473V24.4375C57.473 24.767 57.5223 25.0152 57.6207 25.1818C57.7192 25.3447 57.8461 25.4564 58.0014 25.517C58.1605 25.5739 58.3329 25.6023 58.5185 25.6023C58.6548 25.6023 58.7741 25.5928 58.8764 25.5739C58.9787 25.5549 59.0582 25.5398 59.1151 25.5284L59.4219 26.9318C59.3234 26.9697 59.1832 27.0076 59.0014 27.0455C58.8196 27.0871 58.5923 27.1098 58.3196 27.1136C57.8726 27.1212 57.456 27.0417 57.0696 26.875C56.6832 26.7083 56.3707 26.4508 56.1321 26.1023C55.8935 25.7538 55.7741 25.3163 55.7741 24.7898V16.1818Z"
                  fill="#FF0000"
                />
                <path
                  d="M76 14C80.125 14 83.5 17.375 83.5 21.5C83.5 25.6562 80.125 29 76 29C71.8438 29 68.5 25.6562 68.5 21.5C68.5 17.375 71.8438 14 76 14ZM70 21.5C70 24.8125 72.6875 27.5 76 27.5C77.375 27.5 78.6562 27.0312 79.6562 26.25L71.25 17.8438C70.4688 18.8438 70 20.125 70 21.5ZM80.7188 25.1875C81.5 24.1875 82 22.9062 82 21.5C82 18.2188 79.2812 15.5 76 15.5C74.5938 15.5 73.3125 16 72.3125 16.7812L80.7188 25.1875Z"
                  fill="#FF0000"
                />
              </svg>
            </button>
            <button
              onClick={() => {
                handleVerifyDocument(document._id, "verified");
              }}
              className="bg-green-300/20 border-green-500 border-2  hover:bg-green-800 px-4 text-green-500 font-medium p-2 rounded flex items-center"
            >
              Verify <Check className="w-4 h-4 ml-2" />
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default DocumentView;
